import React, { useEffect, useState, useCallback, useRef } from 'react';
import api from '../../utils/axios';
import { toast } from 'sonner';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from '../../components/ui/dialog';
import {
  Boxes, RefreshCw, Loader2, Eye, Wallet, AlertTriangle, HandCoins, Clock,
} from 'lucide-react';
import { fmtMoney, fmtK, FiltrosPrestamos, InformeButtons } from './_prestamosUtils';

const DATE_FIELDS = [
  { value: '', label: 'Sin filtro de fecha' },
  { value: 'solicitud', label: 'Fecha de solicitud' },
  { value: 'aprobacion', label: 'Fecha de aprob./rechazo' },
  { value: 'mora', label: 'Fecha de mora' },
  { value: 'vencimiento', label: 'Vencimiento de cuota' },
];

const estadoCuotaBadge = (est) => {
  if (est === 'abonada') return <Badge className="bg-emerald-100 text-emerald-700 border-0">Abonada</Badge>;
  if (est === 'mora') return <Badge className="bg-rose-100 text-rose-700 border-0">Mora</Badge>;
  return <Badge className="bg-blue-100 text-blue-700 border-0">Pendiente</Badge>;
};

const estadoLoanColor = (e) => ({
  Mora: 'bg-rose-100 text-rose-700', Vigente: 'bg-emerald-100 text-emerald-700',
  Cancelado: 'bg-slate-100 text-slate-500', Autorizado: 'bg-blue-100 text-blue-700',
  Rechazado: 'bg-amber-100 text-amber-700',
}[e] || 'bg-slate-100 text-slate-600');

const Stat = ({ icon: Icon, label, value, sub, tone = 'slate', testid }) => {
  const tones = { slate: 'text-slate-800 dark:text-slate-100', emerald: 'text-emerald-600', rose: 'text-rose-600', amber: 'text-amber-600', teal: 'text-teal-600' };
  return (
    <Card className="p-4 border-slate-200 dark:border-slate-800" data-testid={testid}>
      <div className="flex items-center gap-2 mb-1 text-slate-500"><Icon className="h-4 w-4" /><span className="text-xs font-medium">{label}</span></div>
      <p className={`text-xl font-bold ${tones[tone]}`}>{value}</p>
      {sub && <p className="text-[11px] text-slate-400 mt-0.5">{sub}</p>}
    </Card>
  );
};

const InventarioPrestamos = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filtros, setFiltros] = useState({ date_field: '', desde: '', hasta: '', estado: '', frecuencia: '', q: '' });
  const [recalc, setRecalc] = useState(false);
  const [detalle, setDetalle] = useState(null);
  const [detLoading, setDetLoading] = useState(false);
  const pollRef = useRef(null);

  const load = useCallback(async (f) => {
    setLoading(true);
    try {
      const r = await api.get('/motor/inventario', { params: f || filtros });
      setData(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'No se pudo cargar el inventario');
    } finally {
      setLoading(false);
    }
  }, [filtros]);

  useEffect(() => { load(filtros); return () => { if (pollRef.current) clearInterval(pollRef.current); }; }, []); // eslint-disable-line

  const abrirDetalle = async (id) => {
    setDetLoading(true); setDetalle({ prestamo_id: id });
    try {
      const r = await api.get(`/motor/inventario/${id}`);
      setDetalle(r.data);
    } catch (e) {
      toast.error('No se pudo cargar el detalle del préstamo');
      setDetalle(null);
    } finally { setDetLoading(false); }
  };

  const recalcular = async () => {
    setRecalc(true);
    try {
      await api.post('/kpis/automatizaciones/prestamos-detalle-mora/ejecutar');
      toast.info('Actualizando: bajando el detalle de cuotas y pagos desde Waya…');
      let intentos = 0;
      pollRef.current = setInterval(async () => {
        intentos += 1;
        if (intentos > 50) { clearInterval(pollRef.current); pollRef.current = null; setRecalc(false); toast.error('Está tardando demasiado. Reintentá en unos minutos.'); load(filtros); return; }
        try {
          const r = await api.get('/kpis/prestamos/detalle');
          const st = r.data?.scrape?.status;
          if (st && st !== 'en_ejecucion') {
            clearInterval(pollRef.current); pollRef.current = null; setRecalc(false);
            if (st === 'error') toast.error('Falló la sincronización con Waya'); else toast.success('Inventario actualizado desde Waya');
            load(filtros);
          }
        } catch (e) { /* seguir */ }
      }, 6000);
    } catch (e) { setRecalc(false); toast.error(e?.response?.data?.detail || 'No se pudo iniciar la actualización'); }
  };

  const items = data?.items || [];
  const a = data?.agregados || {};
  const d = detalle;

  return (
    <div className="p-6 lg:p-8 pt-20 sm:pt-6 lg:pt-8 max-w-7xl mx-auto" data-testid="inventario-prestamos">
      <div className="mb-5 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div className="flex items-center gap-3">
          <div className="p-2.5 rounded-xl bg-teal-500/10"><Boxes className="h-6 w-6 text-teal-600" /></div>
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold tracking-tight text-slate-900 dark:text-white">Inventario de Préstamos</h1>
            <p className="text-sm text-slate-500">Prestado · cobrado · pendiente y mora por cliente{data?.hoy ? ` · al ${data.hoy}` : ''}</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <InformeButtons tipo="inventario" params={filtros} />
          <Button size="sm" className="bg-teal-600 hover:bg-teal-700 text-white" onClick={recalcular} disabled={recalc} data-testid="inv-recalcular-btn">
            {recalc ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <RefreshCw className="h-4 w-4 mr-2" />}
            {recalc ? 'Actualizando…' : 'Actualizar (Waya)'}
          </Button>
        </div>
      </div>

      <FiltrosPrestamos value={filtros} onChange={setFiltros} onApply={(f) => load(f)} dateFields={DATE_FIELDS} />

      {/* Agregados */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-3">
        <Stat icon={Wallet} label="Prestado" value={fmtK(a.total_prestado)} sub={`${a.total_prestamos || 0} préstamos`} testid="inv-total-prestado" />
        <Stat icon={HandCoins} label="Cobrado" value={fmtK(a.total_cobrado)} tone="emerald" testid="inv-total-cobrado" />
        <Stat icon={Clock} label="Pendiente (no mora)" value={fmtK(a.total_pendiente_no_mora)} tone="amber" testid="inv-total-pendiente" />
        <Stat icon={AlertTriangle} label="Mora consolidada" value={fmtK(a.total_mora)} tone="rose" sub={`${a.prestamos_con_mora || 0} en mora · ${a.tasa_mora || 0}%`} testid="inv-total-mora" />
        <Stat icon={AlertTriangle} label="Tasa de mora" value={`${a.tasa_mora || 0}%`} tone="rose" />
      </div>

      {/* Total de préstamos + desglose por estado */}
      {a.por_estado && Object.keys(a.por_estado).length > 0 && (
        <div className="flex flex-wrap items-center gap-2 mb-5" data-testid="inv-por-estado">
          <span className="text-sm font-medium text-slate-600 dark:text-slate-300">
            {a.total_prestamos || 0} préstamo(s):
          </span>
          {Object.entries(a.por_estado)
            .sort((x, y) => y[1] - x[1])
            .map(([est, cnt]) => (
              <Badge key={est} className={`${estadoLoanColor(est)} border-0`} data-testid={`inv-estado-${est}`}>
                {est}: <span className="font-bold ml-1">{cnt}</span>
              </Badge>
            ))}
        </div>
      )}

      {loading ? (
        <div className="text-center text-slate-500 py-16"><Loader2 className="h-6 w-6 animate-spin mx-auto" /></div>
      ) : items.length === 0 ? (
        <div className="text-center text-slate-400 py-16" data-testid="inv-empty">
          <Boxes className="h-10 w-10 mx-auto mb-3 opacity-40" />
          Sin préstamos para los filtros actuales. Si es la primera vez, usá "Actualizar (Waya)".
        </div>
      ) : (
        <Card className="border-slate-200 dark:border-slate-800 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 dark:bg-slate-800 text-slate-500">
                <tr>
                  <th className="text-left px-3 py-2.5 font-medium">Cliente</th>
                  <th className="text-center px-3 py-2.5 font-medium">Estado</th>
                  <th className="text-left px-3 py-2.5 font-medium">Frec.</th>
                  <th className="text-right px-3 py-2.5 font-medium">Prestado</th>
                  <th className="text-right px-3 py-2.5 font-medium">Cobrado</th>
                  <th className="text-right px-3 py-2.5 font-medium">Pendiente (no mora)</th>
                  <th className="text-right px-3 py-2.5 font-medium">Mora consolidada</th>
                  <th className="text-center px-3 py-2.5 font-medium">Días</th>
                  <th className="text-center px-3 py-2.5 font-medium"></th>
                </tr>
              </thead>
              <tbody>
                {items.map((it) => (
                  <tr key={it.prestamo_id} className={`border-t border-slate-100 dark:border-slate-800 hover:bg-slate-50 dark:hover:bg-slate-800/50 ${it.en_mora ? 'bg-rose-50/30 dark:bg-rose-950/10' : ''}`} data-testid={`inv-row-${it.prestamo_id}`}>
                    <td className="px-3 py-2.5">
                      <p className="font-medium text-slate-800 dark:text-slate-100">{it.nombre || '—'}</p>
                      <p className="font-mono text-xs text-slate-500">{it.cuit}</p>
                    </td>
                    <td className="px-3 py-2.5 text-center"><Badge className={`${estadoLoanColor(it.estado)} border-0`}>{it.estado || '—'}</Badge></td>
                    <td className="px-3 py-2.5 text-slate-600 dark:text-slate-300">{it.frecuencia || '—'}</td>
                    <td className="px-3 py-2.5 text-right text-slate-700 dark:text-slate-200">{fmtMoney(it.prestado)}</td>
                    <td className="px-3 py-2.5 text-right text-emerald-600">{fmtMoney(it.cobrado)}</td>
                    <td className="px-3 py-2.5 text-right text-amber-600">{it.pendiente_no_mora ? fmtMoney(it.pendiente_no_mora) : '—'}</td>
                    <td className="px-3 py-2.5 text-right font-semibold text-rose-600" data-testid={`inv-mora-${it.prestamo_id}`}>{it.mora_consolidada ? fmtMoney(it.mora_consolidada) : '—'}</td>
                    <td className="px-3 py-2.5 text-center">{it.dias_mora > 0 ? <Badge className="bg-rose-100 text-rose-700 border-0">{it.dias_mora}</Badge> : <span className="text-slate-400">—</span>}</td>
                    <td className="px-3 py-2.5 text-center">
                      <Button variant="ghost" size="sm" className="h-8 text-teal-600" onClick={() => abrirDetalle(it.prestamo_id)} data-testid={`inv-ver-${it.prestamo_id}`}><Eye className="h-4 w-4" /></Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      )}

      {/* Drill-down */}
      <Dialog open={!!detalle} onOpenChange={(o) => { if (!o) setDetalle(null); }}>
        <DialogContent className="max-w-3xl max-h-[92vh] overflow-y-auto" data-testid="inv-detalle-dialog">
          <DialogHeader>
            <DialogTitle>Detalle del préstamo {d?.prestamo_id}</DialogTitle>
            <DialogDescription>Composición de cuotas, mora y pagos (datos sincronizados de Waya).</DialogDescription>
          </DialogHeader>
          {detLoading || !d?.cuit ? (
            <div className="py-10 text-center"><Loader2 className="h-6 w-6 animate-spin mx-auto text-slate-400" /></div>
          ) : (
            <div className="space-y-4">
              <div>
                <p className="text-lg font-bold text-slate-800 dark:text-slate-100">{d.nombre}</p>
                <p className="font-mono text-xs text-slate-500">{d.cuit}{d.telefono ? ` · ${d.telefono}` : ''}</p>
                <p className="text-xs text-slate-400">{d.frecuencia} · {d.cantidad_cuotas} cuotas · Prestado {fmtMoney(d.monto_solicitado)}{d.tna ? ` · TNA ${d.tna}` : ''}</p>
                <p className="text-xs text-slate-400">Solicitado: {d.fecha_solicitud || '—'} · {d.accion_realizada || 'Estado'}: {d.fecha_accion || '—'}{d.usuario_accion ? ` (${d.usuario_accion})` : ''}</p>
              </div>
              <div className="grid grid-cols-3 gap-2">
                <div className="rounded-lg border border-slate-200 dark:border-slate-700 p-2.5"><p className="text-[11px] text-slate-400">Mora consolidada</p><p className="font-bold text-rose-600">{fmtMoney(d.total_mora)}</p></div>
                <div className="rounded-lg border border-slate-200 dark:border-slate-700 p-2.5"><p className="text-[11px] text-slate-400">Punitorios</p><p className="font-semibold text-slate-700 dark:text-slate-200">{fmtMoney(d.punitorios_saldo)}</p></div>
                <div className="rounded-lg border border-slate-200 dark:border-slate-700 p-2.5"><p className="text-[11px] text-slate-400">Días mora</p><p className="font-semibold text-rose-600">{d.dias_mora || 0}</p></div>
              </div>
              <div>
                <p className="text-sm font-semibold text-slate-600 dark:text-slate-300 mb-2">Cuotas</p>
                <div className="space-y-2" data-testid="inv-detalle-cuotas">
                  {(d.cuotas || []).map((c, i) => (
                    <div key={i} className="rounded-lg border border-slate-200 dark:border-slate-700 p-2.5">
                      <div className="flex items-center justify-between">
                        <div>
                          <span className="font-medium text-slate-700 dark:text-slate-200">{c.cuota || c.nro}</span>
                          {c.es_punitorio && <Badge className="ml-2 bg-purple-100 text-purple-700 border-0">Punitorios</Badge>}
                          <span className="ml-2 text-xs text-slate-400">vto {c.fecha_vto}</span>
                        </div>
                        <div className="flex items-center gap-3">
                          <span className="text-xs text-slate-500">Cuota {fmtMoney(c.total)}</span>
                          <span className={`text-sm font-semibold ${c.estado === 'mora' ? 'text-rose-600' : c.estado === 'pendiente' ? 'text-blue-600' : 'text-emerald-600'}`}>Saldo {fmtMoney(c.saldo)}</span>
                          {estadoCuotaBadge(c.estado)}
                        </div>
                      </div>
                      {(c.pagos || []).length > 0 && (
                        <div className="mt-2 pl-3 border-l-2 border-slate-100 dark:border-slate-700 space-y-0.5">
                          {c.pagos.map((p, j) => (
                            <div key={j} className="flex justify-between text-xs text-slate-500">
                              <span>Pago {p.fecha}</span><span className="text-emerald-600">{fmtMoney(p.monto)}</span>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default InventarioPrestamos;
